import * as Sentry from '@sentry/vue'

let activeOperations = 0
let stopTimeout = null

/**
 * Start a Sentry Session Replay if not already active
 */
export function startReplay () {
	const replay = Sentry.getReplay()

	if (!replay) {
		return
	}

	const replayId = replay.getReplayId()

	if (!replayId) {
		replay.start()
	}

	activeOperations++
}

/**
 * Manually increment active operations counter
 * Use this for tracking non-GraphQL operations like S3 uploads
 */
export function incrementOperations () {
	activeOperations++

	if (stopTimeout) {
		clearTimeout(stopTimeout)
		stopTimeout = null
	}
}

/**
 * Manually decrement active operations counter
 */
export function decrementOperations () {
	activeOperations--

	if (activeOperations < 0) {
		activeOperations = 0
	}
}

/**
 * Flush the current Sentry Session Replay
 */
export async function flushReplay () {
	const replay = Sentry.getReplay()

	if (!replay) {
		return
	}

	const replayId = replay.getReplayId()

	if (replayId) {
		await replay.flush()
	}
}

/**
 * Stop the current Sentry Session Replay
 */
export function stopReplay () {
	const replay = Sentry.getReplay()

	if (!replay) {
		return
	}

	const replayId = replay.getReplayId()

	if (replayId) {
		replay.stop()
		activeOperations = 0
	}
}

/**
 * Schedule a delayed stop that can be cancelled by new operations
 */
function scheduleStop (delayMs) {
	if (stopTimeout) {
		clearTimeout(stopTimeout)
	}

	stopTimeout = setTimeout(async () => {
		if (activeOperations === 0) {
			await flushReplay()
			stopReplay()
		}
	}, delayMs)
}

/**
 * Add breadcrumb for GraphQL operations
 */
export function addGraphQLBreadcrumb (operationName, variables = {}, type = 'mutation') {
	Sentry.addBreadcrumb({
		category: 'graphql',
		message: `${type}: ${operationName}`,
		level: 'info',
		data: sanitizeVariables(variables)
	})
}

/**
 * Sanitize variables to remove sensitive data
 */
function sanitizeVariables (variables) {
	const sensitiveKeys = [
		'password',
		'token',
		'secret',
		'auth',
		'session',
		'usercode',
		'confirmationcode',
		'otpauth',
		'softwaretoken',
		'secretcode',
		'sessionkey'
	]
	const sanitized = JSON.parse(JSON.stringify(variables))

	const sanitizeObject = (obj) => {
		Object.keys(obj).forEach(key => {
			if (typeof obj[key] === 'object' && obj[key] !== null) {
				sanitizeObject(obj[key])
			} else if (sensitiveKeys.some(sensitive => key.toLowerCase().includes(sensitive))) {
				obj[key] = '[REDACTED]'
			}
		})
	}

	sanitizeObject(sanitized)
	return sanitized
}

/**
 * Start replay and handle errors with automatic flush
 * Stops replay after delay when all operations complete
 */
export async function withReplay (callback, context = {}) {
	startReplay()

	addGraphQLBreadcrumb(context.operation || 'unknown', context, 'mutation')

	Sentry.setTag('operation', context.operation)
	Sentry.setContext('operationContext', context)

	try {
		const startTime = Date.now()
		const result = await callback()
		const duration = Date.now() - startTime

		Sentry.addBreadcrumb({
			category: 'operation',
			message: `${context.operation} completed successfully`,
			level: 'info',
			data: {
				duration: `${duration}ms`,
				...context
			}
		})

		activeOperations--

		if (activeOperations === 0) {
			scheduleStop(5000)
		}

		return result
	} catch (error) {
		Sentry.addBreadcrumb({
			category: 'operation',
			message: `${context.operation} failed`,
			level: 'error',
			data: {
				error: error.message,
				...context
			}
		})

		await flushReplay()

		Sentry.captureException(error, {
			contexts: {
				operation: context
			},
			tags: {
				operation: context.operation
			}
		})

		activeOperations--

		if (activeOperations === 0) {
			scheduleStop(20000)
		}

		throw error
	}
}

export { Sentry }

export default {
	startReplay,
	flushReplay,
	stopReplay,
	withReplay,
	addGraphQLBreadcrumb,
	incrementOperations,
	decrementOperations,
	Sentry
}
